import React from 'react';

import { connect } from 'react-redux';

import { Input } from '@nerdwallet/currency';
import { NumStepper } from '@nerdwallet/react-stepper';

import { REFINANCE } from '../../../lib/analytics-constants';
import { validationsSelector } from '../../../lib/selectors';
import {
  manualInputsSelector,
  inputsSelector,
} from '../../../lib/selectors/refinance-calculator-selectors';
import {
  RefinanceInput,
  INPUT_TRACKING_TYPES,
  formatYears,
  inputOnChange,
} from '../../../lib/utils';

type OnChangeEvent = React.ChangeEvent<HTMLInputElement>;

type onInputChange = OnChangeEvent | number;

type TrackingTypes = keyof typeof INPUT_TRACKING_TYPES;

type RefinanceLoanTermProps = RefinanceInput & {
  /**
   * MortgageLoanTerm can be shown as:
   *  - TEXT - NumberInput (Onboarding Modal)
   *  - RANGE - Number Stepper (Control Panel)
   */
  inputType?: TrackingTypes;
};

const RefinanceLoanTermInput: React.FC<RefinanceLoanTermProps> = ({
  manualInputs,
  inputs,
  dispatch,
  analytics,
  validations,
  label,
  tooltip,
  fullWidth,
  inputType,
}) => {
  // if inputType is RANGE we need to set a default value, so taking placeholder if needed
  const placeholder = inputs?.refinanceLoanTerm;
  const value =
    inputType === INPUT_TRACKING_TYPES.TEXT
      ? manualInputs?.refinanceLoanTerm ?? ''
      : manualInputs?.refinanceLoanTerm ?? placeholder ?? '';
  const errorMessage = validations?.refinanceLoanTerm?.[0];
  const error = Boolean(errorMessage);
  const analyticsEnabled = analytics && analytics?.enabled;
  const legacyAnalytics =
    analyticsEnabled && analytics?.payload?.legacy?.formName;
  const bedrockAnalytics =
    analyticsEnabled && analytics?.payload?.bedrock?.eventType;

  const onInputChange = (e: onInputChange) => {
    let legacy;
    if (legacyAnalytics) {
      const legacyProps = analytics?.payload?.legacy;
      legacy = {
        ...legacyProps,
        fieldName: 'refinanceLoanTerm',
        inputType,
      };
    }

    let bedrock;
    if (bedrockAnalytics) {
      const bedrockProps = analytics?.payload?.bedrock;
      bedrock = {
        ...bedrockProps,
        valueFieldName: 'value_i',
      };
    }

    inputOnChange({
      event: e,
      dispatch,
      legacy,
      bedrock,
      fieldName: 'refinanceLoanTerm',
      calculator: REFINANCE,
      inputType: 'number',
    });
  };

  if (inputType === INPUT_TRACKING_TYPES.RANGE) {
    return (
      <NumStepper
        editable
        formatter={formatYears}
        max={30}
        min={1}
        stepSize={1}
        value={value}
        onChange={onInputChange}
      />
    );
  }

  return (
    <Input
      inputMode="tel"
      value={value}
      placeholder={placeholder}
      error={error}
      errorMessage={errorMessage}
      onChange={onInputChange}
      label={label}
      tooltip={tooltip}
      fullWidth={fullWidth}
    />
  );
};

const mapStateToProps = (state) => ({
  manualInputs: manualInputsSelector(state),
  inputs: inputsSelector(state),
  validations: validationsSelector(state),
});

RefinanceLoanTermInput.defaultProps = {
  inputType: 'TEXT',
};

export default connect(mapStateToProps)(RefinanceLoanTermInput);
